import React, { useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Paper,
  Alert,
  LinearProgress,
  Stack,
  Chip,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import SaveIcon from "@mui/icons-material/Save";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { ValidatedTextField } from "../common/inputs/validatedTextField";
import { useNumberSerialiser } from "../../hooks/serialisers/numbers";
import { useStringSerialiser } from "../../hooks/serialisers/strings";
import { useTenant } from "../../hooks/settings";
import { useAudioRecorder } from "./hooks/useAudioRecorder";
import { usePhotoCapture } from "./hooks/usePhotoCapture";
import { useGeminiAI } from "./hooks/useGeminiAI";
import {
  DeliveryFormSave,
  GetUploadedPhotoApiResponse,
  useGenerateDeliveryFormNameMutation,
} from "src/store/api/generatedApi";

interface AudioClip {
  id: string;
  blob: Blob;
  timestamp: number;
  processed: boolean;
}

interface DeliveryFormProps {
  formData: DeliveryFormSave;
  onSave: (data: DeliveryFormSave) => void;
  onCompleteDelivery: (data: DeliveryFormSave) => void;
  onFieldUpdate: (
    field: keyof DeliveryFormSave,
    value: string | number
  ) => void;
  capturedPhoto?: File | null;
  onPhotoCapture: (file: File) => void;
  uploadedPhoto?: GetUploadedPhotoApiResponse;
  isLoadingPhoto?: boolean;
}

export const DeliveryForm: React.FC<DeliveryFormProps> = ({
  formData,
  onSave,
  onCompleteDelivery,
  onFieldUpdate,
  capturedPhoto,
  onPhotoCapture,
  uploadedPhoto,
  isLoadingPhoto,
}) => {
  const tenantName = useTenant();

  // Serializers for form validation
  const stringSerializer = useStringSerialiser({
    allowEmpty: true,
    trim: true,
  });
  const numberSerializer = useNumberSerialiser({ min: 0, decimalPlaces: 2 });

  // API mutations
  const [generateName] = useGenerateDeliveryFormNameMutation();

  // Audio recording state
  const [isRecording, setIsRecording] = React.useState(false);
  const [audioClips, setAudioClips] = React.useState<AudioClip[]>([]);
  const [processingCount, setProcessingCount] = React.useState(0);
  const [isGeneratingName, setIsGeneratingName] = React.useState(false);

  // Custom hooks
  const {
    startRecording,
    stopRecording,
    isSupported: audioSupported,
  } = useAudioRecorder({
    onAudioChunk: (blob: Blob) => {
      const newClip: AudioClip = {
        id: `clip_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        blob,
        timestamp: Date.now(),
        processed: false,
      };
      console.log("newClip", newClip);
      setAudioClips((prev) => [...prev, newClip]);
    },
    chunkDuration: 3000, // 3 seconds for more responsive live processing
  });

  const { capturePhoto, isSupported: cameraSupported } = usePhotoCapture({
    onPhotoCapture,
  });

  // Gemini AI integration
  const { processAudioClip } = useGeminiAI({
    onFieldUpdate: (
      field: string | number | symbol,
      value: string | number
    ) => {
      onFieldUpdate(field as keyof DeliveryFormSave, value);
    },
    currentFormData: formData,
    onSaveAndNewDelivery: async () => {
      // This will be handled by the parent component
      onSave(formData);
    },
    onOpenPreviousDelivery: async () => {
      // This will be handled by the parent component
    },
    savedDeliveries: [], // This will be provided by parent if needed
  });

  // Process new audio clips immediately when they arrive
  useEffect(() => {
    const unprocessedClips = audioClips.filter((clip) => !clip.processed);

    // Process each unprocessed clip immediately
    unprocessedClips.forEach((clip) => {
      // Avoid processing the same clip multiple times
      if (clip.processed) return;

      // Mark as being processed to prevent duplicate processing
      setAudioClips((prev) =>
        prev.map((c) => (c.id === clip.id ? { ...c, processed: true } : c))
      );

      setProcessingCount((prev) => prev + 1);

      // Process the clip asynchronously
      processAudioClip(clip.blob)
        .then(() => {
          console.log(`Successfully processed audio clip: ${clip.id}`);
        })
        .catch((error) => {
          console.error("Error processing audio clip:", error);
          // Mark as unprocessed again on error so user can retry
          setAudioClips((prev) =>
            prev.map((c) => (c.id === clip.id ? { ...c, processed: false } : c))
          );
        })
        .finally(() => {
          setProcessingCount((prev) => prev - 1);
        });
    });
  }, [audioClips]);

  // Handle recording toggle
  const handleRecordingToggle = useCallback(async () => {
    if (isRecording) {
      await stopRecording();
      setIsRecording(false);
    } else {
      await startRecording();
      setIsRecording(true);
    }
  }, [isRecording, startRecording, stopRecording]);

  // Handle form field updates
  const updateField = useCallback(
    (field: keyof DeliveryFormSave) => (value: string | number) => {
      onFieldUpdate(field, value);
    },
    [onFieldUpdate]
  );

  // Generate automatic name using AI agent
  const handleGenerateName = useCallback(async () => {
    setIsGeneratingName(true);
    try {
      const result = await generateName({
        tenantName,
        deliveryFormSave: formData,
      }).unwrap();

      if (result.success && result.name) {
        onFieldUpdate("name", result.name);
      } else {
        console.error(
          "Failed to generate name:",
          result.error || "Unknown error"
        );
      }
    } catch (error) {
      console.error("Error generating name:", error);
    } finally {
      setIsGeneratingName(false);
    }
  }, [formData, onFieldUpdate, generateName, tenantName]);

  // Clear audio clips
  const clearAudioClips = useCallback(() => {
    setAudioClips([]);
  }, []);

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Left Column: Form Fields */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card sx={{ height: "fit-content" }}>
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                Delivery Information
              </Typography>

              {/* Delivery Name Field */}
              <Box sx={{ mb: 3 }}>
                <Stack direction="row" spacing={1} alignItems="flex-end">
                  <Box sx={{ flex: 1 }}>
                    <ValidatedTextField
                      label="Delivery Name"
                      value={formData.name ?? ""}
                      setValue={updateField("name")}
                      serialiser={stringSerializer}
                      textFieldProps={{
                        fullWidth: true,
                        placeholder: "e.g., Bay 3 - Steel - 5.2t - Truck 101",
                      }}
                    />
                  </Box>
                  <Tooltip title="Generate automatic name based on form data">
                    <IconButton
                      onClick={handleGenerateName}
                      disabled={isGeneratingName}
                      color="primary"
                      sx={{
                        height: 36,
                        border: "1px solid",
                        borderColor: "primary.main",
                        borderRadius: 1,
                        "&:hover": {
                          backgroundColor: "primary.main",
                          color: "white",
                        },
                      }}
                    >
                      <AutoAwesomeIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
                {isGeneratingName ? (
                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    Generating name...
                  </Typography>
                ) : null}
              </Box>

              {/* Form Grid - 2 columns on iPad */}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <ValidatedTextField
                    label="Bay Number"
                    value={formData.bay ?? ""}
                    setValue={updateField("bay")}
                    serialiser={stringSerializer}
                    textFieldProps={{ fullWidth: true }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <ValidatedTextField
                    label="Truck Number"
                    value={formData.truck_number ?? ""}
                    setValue={updateField("truck_number")}
                    serialiser={stringSerializer}
                    textFieldProps={{ fullWidth: true }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <ValidatedTextField
                    label="Commodity"
                    value={formData.commodity ?? ""}
                    setValue={updateField("commodity")}
                    serialiser={stringSerializer}
                    textFieldProps={{ fullWidth: true }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <ValidatedTextField
                    label="Weight (lbs)"
                    value={formData.mass_lbs ?? 0}
                    setValue={updateField("mass_lbs")}
                    serialiser={numberSerializer}
                    textFieldProps={{ fullWidth: true }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <ValidatedTextField
                    label="Deviations"
                    value={formData.deviations ?? ""}
                    setValue={updateField("deviations")}
                    serialiser={stringSerializer}
                    textFieldProps={{ fullWidth: true }}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <ValidatedTextField
                    label="Free Text / Notes"
                    value={formData.notes ?? ""}
                    setValue={updateField("notes")}
                    serialiser={stringSerializer}
                    textFieldProps={{
                      fullWidth: true,
                      multiline: true,
                      rows: 4,
                    }}
                  />
                </Grid>
              </Grid>

              {/* Submit button */}
              <Box sx={{ textAlign: "center", pt: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<SaveIcon />}
                  onClick={() => onCompleteDelivery(formData)}
                  sx={{ minWidth: 200, py: 1.5 }}
                >
                  Complete Delivery
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column: Controls & Status */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={3}>
            {/* Photo Capture Section */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Photo Documentation
              </Typography>
              <Box sx={{ textAlign: "center" }}>
                {/* Display photo - prioritize captured photo over uploaded photo */}
                {capturedPhoto ? (
                  <Box sx={{ mb: 2 }}>
                    <Box
                      component="img"
                      src={URL.createObjectURL(capturedPhoto)}
                      alt="Captured delivery photo"
                      sx={{
                        width: "100%",
                        maxWidth: 300,
                        height: "auto",
                        borderRadius: 1,
                        border: 1,
                        borderColor: "grey.300",
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="primary"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      New photo (not yet saved)
                    </Typography>
                  </Box>
                ) : uploadedPhoto ? (
                  <Box sx={{ mb: 2 }}>
                    <Box
                      component="img"
                      src={`data:${uploadedPhoto.type};base64,${uploadedPhoto.file.content}`}
                      alt="Uploaded delivery photo"
                      sx={{
                        width: "100%",
                        maxWidth: 300,
                        height: "auto",
                        borderRadius: 1,
                        border: 1,
                        borderColor: "grey.300",
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="success.main"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      ✓ Saved photo: {uploadedPhoto.file.name || "Photo"}
                    </Typography>
                  </Box>
                ) : isLoadingPhoto ? (
                  <Box sx={{ mb: 2, p: 3 }}>
                    <LinearProgress />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      Loading photo...
                    </Typography>
                  </Box>
                ) : null}

                <Button
                  variant="outlined"
                  startIcon={<PhotoCameraIcon />}
                  onClick={capturePhoto}
                  disabled={!cameraSupported}
                  size="large"
                  fullWidth
                  sx={{ py: 2, mb: 2 }}
                >
                  {capturedPhoto ? "Change Photo" : "Take Photo"}
                </Button>
                {capturedPhoto ? (
                  <Typography
                    variant="body2"
                    color="success.main"
                    sx={{ fontWeight: "fontWeightMedium" }}
                  >
                    ✓ Photo captured: {capturedPhoto.name}
                  </Typography>
                ) : null}
                {!cameraSupported && (
                  <Typography variant="caption" color="error" display="block">
                    Camera not supported on this device
                  </Typography>
                )}
              </Box>
            </Paper>

            {/* Audio Recording Section */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Audio Recording
              </Typography>

              {/* Recording Status */}
              {isRecording ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <MicIcon color="error" />
                    <Typography>Recording audio...</Typography>
                  </Stack>
                  {processingCount > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="primary">
                        🎤 Live processing {processingCount} clip
                        {processingCount > 1 ? "s" : ""}...
                      </Typography>
                      <LinearProgress sx={{ mt: 0.5 }} color="primary" />
                    </Box>
                  )}
                </Alert>
              ) : null}

              {/* Recording Button */}
              <Box sx={{ textAlign: "center", mb: 2 }}>
                <Button
                  variant={isRecording ? "contained" : "outlined"}
                  color={isRecording ? "secondary" : "primary"}
                  size="large"
                  fullWidth
                  startIcon={isRecording ? <MicOffIcon /> : <MicIcon />}
                  onClick={handleRecordingToggle}
                  disabled={!audioSupported}
                  sx={{
                    py: 2,
                    animation: isRecording ? "pulse 2s infinite" : "none",
                    "@keyframes pulse": {
                      "0%": { transform: "scale(1)" },
                      "50%": { transform: "scale(1.02)" },
                      "100%": { transform: "scale(1)" },
                    },
                  }}
                >
                  {isRecording ? "Stop Recording" : "Start Recording"}
                </Button>
              </Box>

              {!audioSupported && (
                <Alert severity="warning">
                  Audio recording is not supported on this device or browser.
                </Alert>
              )}

              {/* Audio Clips Status */}
              {audioClips.length > 0 && (
                <Box>
                  <Divider sx={{ my: 2 }} />
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 1 }}
                  >
                    <AudiotrackIcon fontSize="small" />
                    <Typography variant="subtitle2">
                      {audioClips.length} clips recorded,{" "}
                      {audioClips.filter((c) => c.processed).length} processed
                      {processingCount > 0 && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="primary"
                          sx={{ ml: 1 }}
                        >
                          ({processingCount} processing live)
                        </Typography>
                      )}
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    sx={{ mb: 2 }}
                  >
                    {audioClips.slice(-6).map((clip) => (
                      <Chip
                        key={clip.id}
                        label={new Date(clip.timestamp).toLocaleTimeString()}
                        color={clip.processed ? "success" : "primary"}
                        size="small"
                        variant={clip.processed ? "filled" : "outlined"}
                        sx={{
                          animation: !clip.processed
                            ? "pulse 1.5s infinite"
                            : "none",
                          "@keyframes pulse": {
                            "0%": { opacity: 1 },
                            "50%": { opacity: 0.6 },
                            "100%": { opacity: 1 },
                          },
                        }}
                      />
                    ))}
                    {audioClips.length > 6 && (
                      <Chip
                        label={`+${audioClips.length - 6} more`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Stack>

                  <Button
                    size="small"
                    onClick={clearAudioClips}
                    variant="outlined"
                    fullWidth
                  >
                    Clear All Clips
                  </Button>
                </Box>
              )}
            </Paper>

            {/* AI Processing Status
                        {(isProcessing || processingCount > 0) && (
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    AI Processing
                                </Typography>
                                <Stack spacing={1}>
                                    <Typography variant="body2" color="text.secondary">
                                        AI is analyzing audio clips and updating form fields...
                                    </Typography>
                                    <LinearProgress />
                                </Stack>
                            </Paper>
                        )} */}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};
